import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Users, GraduationCap, ArrowRight, Download, CheckCircle, Star, Monitor, Euro, Shield, BookOpen, ExternalLink, Award, TrendingUp, Quote, Coffee } from 'lucide-react';
import { generateTrainingPDF } from '@/lib/generateTrainingPDF';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import type { EligibleTraining } from '@/data/eligibleTrainings';
import { TrainingContactForm } from '@/components/training/TrainingContactForm';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  training: EligibleTraining;
}

export const EligibleTrainingDetailPage: React.FC<Props> = ({ training }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleEnroll = () => {
    setShowComingSoon(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={`${training.title} | Gaius`}
        description={training.subtitle}
        keywords={fr ? `formation ia avocat, ${training.title.toLowerCase()}, opco, fif-pl, cpf` : `legal ai training, ${training.title.toLowerCase()}, opco, fif-pl, cpf`}
        canonicalUrl={`https://gaius-tech.com${fr
          ? ({
              'legal-design-ia': '/formation-legal-design-ia-avocat',
              'ia-taches-contractuelles': '/formation-ia-contrat-avocat',
              'ia-contentieux': '/formation-ia-contentieux-avocat',
              'ia-recherche-juridique': '/formation-ia-recherche-juridique',
              'ia-site-web-avocat': '/formation-ia-site-web-avocat',
              'ia-securite-secret-professionnel': '/formation-ia-securite-secret-professionnel',
            }[training.slug] || `/formation-ia-juridique-eligible-financement/${training.slug}`)
          : ({
              'legal-design-ai': '/legal-design-ai-training',
              'ai-contractual-tasks': '/ai-contract-drafting-training',
              'ai-litigation': '/ai-litigation-training',
              'ai-legal-research': '/ai-legal-research-training',
              'ai-lawyer-website': '/ai-lawyer-website-training',
              'ai-security-professional-secrecy': '/ai-security-professional-secrecy-training',
            }[training.slug] || `/legal-ai-training-eligible-funding/${training.slug}`)
        }`}
        alternateUrlFr={`https://gaius-tech.com${({
          'legal-design-ia': '/formation-legal-design-ia-avocat', 'legal-design-ai': '/formation-legal-design-ia-avocat',
          'ia-taches-contractuelles': '/formation-ia-contrat-avocat', 'ai-contractual-tasks': '/formation-ia-contrat-avocat',
          'ia-contentieux': '/formation-ia-contentieux-avocat', 'ai-litigation': '/formation-ia-contentieux-avocat',
          'ia-recherche-juridique': '/formation-ia-recherche-juridique', 'ai-legal-research': '/formation-ia-recherche-juridique',
          'ia-site-web-avocat': '/formation-ia-site-web-avocat', 'ai-lawyer-website': '/formation-ia-site-web-avocat',
          'ia-securite-secret-professionnel': '/formation-ia-securite-secret-professionnel', 'ai-security-professional-secrecy': '/formation-ia-securite-secret-professionnel',
        }[training.slug] || `/formation-ia-juridique-eligible-financement/${training.slug}`)}`}
        alternateUrlEn={`https://gaius-tech.com${({
          'legal-design-ia': '/legal-design-ai-training', 'legal-design-ai': '/legal-design-ai-training',
          'ia-taches-contractuelles': '/ai-contract-drafting-training', 'ai-contractual-tasks': '/ai-contract-drafting-training',
          'ia-contentieux': '/ai-litigation-training', 'ai-litigation': '/ai-litigation-training',
          'ia-recherche-juridique': '/ai-legal-research-training', 'ai-legal-research': '/ai-legal-research-training',
          'ia-site-web-avocat': '/ai-lawyer-website-training', 'ai-lawyer-website': '/ai-lawyer-website-training',
          'ia-securite-secret-professionnel': '/ai-security-professional-secrecy-training', 'ai-security-professional-secrecy': '/ai-security-professional-secrecy-training',
        }[training.slug] || `/legal-ai-training-eligible-funding/${training.slug}`)}`}
      />

      {/* Hero */}
      <section className="bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <div className="flex items-center gap-2 text-primary-foreground/60 text-sm mb-4">
            <Link to={fr ? '/formations' : '/training'} className="hover:text-primary-foreground">{fr ? 'Formations' : 'Training'}</Link>
            <span>/</span>
            <Link to={fr ? '/formations/eligibles' : '/training/eligible'} className="hover:text-primary-foreground">{fr ? 'Formations éligibles' : 'Eligible programs'}</Link>
            <span>/</span>
            <span className="text-primary-foreground/80">{training.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-accent text-accent-foreground">IA</Badge>
                <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground">{training.level}</Badge>
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-3">{training.title}</h1>
              <p className="text-lg text-primary-foreground/80 mb-6">{training.subtitle}</p>
              <div className="flex flex-wrap gap-4 text-sm text-primary-foreground/70">
                <div className="flex items-center gap-1.5"><Monitor className="w-4 h-4" /><span>{fr ? 'DISTANCIEL' : 'REMOTE'}</span></div>
                <div className="flex items-center gap-1.5"><Clock className="w-4 h-4" /><span>{training.durationRemote}</span></div>
                <div className="flex items-center gap-1.5"><Users className="w-4 h-4" /><span>{training.audience}</span></div>
                <div className="flex items-center gap-1.5"><Star className="w-4 h-4" /><span>{training.level}</span></div>
              </div>
            </div>

            {/* Pricing card */}
            <div className="bg-card text-foreground rounded-xl p-6 shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <Monitor className="w-5 h-5 text-primary" />
                <span className="text-sm font-semibold text-foreground">{fr ? 'WEBINAIRE DISTANCIEL' : 'REMOTE WEBINAR'}</span>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" /><span>{training.durationRemote}</span></div>
                <div className="text-3xl font-bold text-foreground">{training.priceRemote}</div>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="flex items-center gap-1.5"><Coffee className="w-3 h-3" />{fr ? 'Pauses et pause-déjeuner incluses' : 'Breaks and lunch break included'}</p>
                  <p>{fr ? 'Accès au replay pendant 30 jours' : 'Replay access for 30 days'}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="outline" className="text-xs">OPCO</Badge>
                <Badge variant="outline" className="text-xs">FIF-PL</Badge>
                <Badge variant="outline" className="text-xs">CPF</Badge>
              </div>
              <Button onClick={handleEnroll} className="w-full mb-2" size="lg">{fr ? "S'inscrire" : 'Enroll'}</Button>
              <Button asChild variant="outline" className="w-full" size="sm">
                <Link to="/contact">{fr ? 'Demande de devis' : 'Request a quote'}</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Sub-navigation */}
        <div className="border-t border-primary-foreground/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-6 overflow-x-auto text-sm py-3">
              <a href="#introduction" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Présentation' : 'Overview'}</a>
              <a href="#objectifs" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Objectifs' : 'Objectives'}</a>
              <a href="#programme" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Programme' : 'Program'}</a>
              <a href="#resultats" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Résultats' : 'Results'}</a>
              <a href="#debouches" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Débouchés' : 'Outcomes'}</a>
              <a href="#public" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Pour qui ?' : 'For whom?'}</a>
              <a href="#financement" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Financement' : 'Funding'}</a>
              <a href="#mentions" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">{fr ? 'Mentions' : 'Info'}</a>
              <a href="#faq" className="text-primary-foreground/70 hover:text-primary-foreground whitespace-nowrap">FAQ</a>
              <Button variant="secondary" size="sm" className="ml-auto flex items-center gap-1" onClick={async () => { await generateTrainingPDF(training, fr ? 'fr' : 'en'); }}>
                <Download className="w-3 h-3" />
                {fr ? 'Programme PDF' : 'PDF Program'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section id="introduction" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">
            {fr ? 'Présentation de la formation' : 'Training overview'}
          </h2>
          <div className="space-y-4">
            {training.introduction.map((paragraph, i) => (
              <p key={i} className="text-muted-foreground leading-relaxed">{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section id="objectifs" className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">{fr ? 'Objectifs pédagogiques' : 'Learning objectives'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {training.objectives.map((obj, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground text-sm">{obj}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program */}
      <section id="programme" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">{fr ? 'Programme détaillé' : 'Detailed program'}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {training.program.map((section, i) => (
              <AccordionItem key={i} value={`prog-${i}`} className="bg-card rounded-xl border border-border px-6">
                <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    {section.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 pl-10">
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-primary mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Expected Results */}
      <section id="resultats" className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">{fr ? 'Résultats attendus' : 'Expected results'}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {training.expectedResults.map((r, i) => (
              <Card key={i} className="border-border text-center">
                <CardContent className="p-5">
                  <TrendingUp className="w-5 h-5 text-primary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary mb-1">{r.metric}</div>
                  <p className="text-xs text-muted-foreground">{r.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Testimonial */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <Quote className="w-6 h-6 text-primary mb-3" />
              <blockquote className="text-foreground italic mb-4 leading-relaxed">
                « {training.trustElements.testimonial.quote} »
              </blockquote>
              <div className="text-sm">
                <span className="font-semibold text-foreground">{training.trustElements.testimonial.author}</span>
                <span className="text-muted-foreground"> — {training.trustElements.testimonial.role}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-center">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-foreground">{training.trustElements.satisfaction}</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {training.trustElements.certifications.map((cert, i) => (
                <Badge key={i} className="bg-primary/10 text-primary border-primary/20">{cert}</Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Professional Outcomes */}
      <section id="debouches" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">{fr ? 'Débouchés professionnels' : 'Professional outcomes'}</h2>
          <p className="text-muted-foreground mb-6">
            {fr ? 'À l\'issue de cette formation, les participants seront en mesure de :' : 'Upon completion, participants will be able to:'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {training.professionalOutcomes.map((outcome, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <ArrowRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground text-sm">{outcome}</span>
              </div>
            ))}
          </div>

          {/* Attestation */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                {fr ? 'Attestation délivrée' : 'Certificate issued'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{training.attestation}</p>
            </CardContent>
          </Card>
        </div>
      </section>
      {/* Audience & Prerequisites */}
      <section id="public" className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {fr ? 'Public concerné' : 'Target audience'}
                </h3>
                <p className="text-sm text-muted-foreground">{training.audience}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {fr ? 'Prérequis' : 'Prerequisites'}
                </h3>
                <p className="text-sm text-muted-foreground">{training.prerequisites}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Funding */}
      <section id="financement" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8">{fr ? 'Options de financement' : 'Funding options'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {[
              { name: 'OPCO', desc: fr ? 'Avocats salariés et juristes d\'entreprise. Prise en charge totale ou partielle selon votre OPCO.' : 'Employed lawyers and in-house counsel. Full or partial coverage depending on your OPCO.' },
              { name: 'FIF-PL', desc: fr ? 'Avocats libéraux. Financement jusqu\'à 100% selon les plafonds annuels du FIF-PL.' : 'Independent lawyers. Up to 100% funding depending on FIF-PL annual caps.' },
              { name: 'CPF', desc: fr ? 'Compte Personnel de Formation. Utilisation de vos droits acquis pour financer la formation.' : 'Personal Training Account. Use your acquired rights to fund the training.' },
            ].map((f, i) => (
              <Card key={i} className="border-border">
                <CardContent className="p-5">
                  <Badge className="mb-3">{f.name}</Badge>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Conformity note */}
          <Card className="border-border bg-secondary/30">
            <CardContent className="p-6">
              <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                {fr ? 'Conformité et qualité' : 'Compliance and quality'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {fr
                  ? 'Gaius est un organisme de formation certifié Qualiopi au titre de la catégorie « Actions de formation ». Cette certification atteste de la conformité de nos processus aux exigences du Référentiel National Qualité (RNQ), condition indispensable pour l\'éligibilité aux financements publics et mutualisés (OPCO, FIF-PL, CPF).'
                  : 'Gaius is a Qualiopi-certified training organization under the "Training actions" category. This certification attests compliance with the National Quality Reference Framework (RNQ), a prerequisite for public and pooled funding eligibility (OPCO, FIF-PL, CPF).'}
              </p>

              {/* Backlinks */}
              <h4 className="text-sm font-semibold text-foreground mb-2">{fr ? 'Ressources officielles' : 'Official resources'}</h4>
              <ul className="space-y-2">
                {training.backlinks.map((link, i) => (
                  <li key={i}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm text-primary hover:underline group">
                      <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-medium">{link.label}</span>
                        <span className="text-muted-foreground"> — {link.description}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* RNCP Mandatory Mentions */}
      <section id="mentions" className="py-16 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">{fr ? 'Mentions obligatoires' : 'Mandatory information'}</h2>
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold text-foreground">{fr ? 'Code formation' : 'Training code'}:</span> <span className="text-muted-foreground">{training.rncp.codeFormation}</span></div>
                <div><span className="font-semibold text-foreground">{fr ? 'Code NSF' : 'NSF code'}:</span> <span className="text-muted-foreground">{training.rncp.codeNSF}</span></div>
                <div className="sm:col-span-2"><span className="font-semibold text-foreground">Formacodes:</span> <span className="text-muted-foreground">{training.rncp.formacodesPrincipaux.join(' · ')}</span></div>
                <div className="sm:col-span-2"><span className="font-semibold text-foreground">{fr ? 'Accessibilité' : 'Accessibility'}:</span> <span className="text-muted-foreground">{training.rncp.accessibilite}</span></div>
                <div className="sm:col-span-2"><span className="font-semibold text-foreground">{fr ? 'Modalités d\'évaluation' : 'Assessment methods'}:</span> <span className="text-muted-foreground">{training.rncp.modalitesEvaluation}</span></div>
                <div><span className="font-semibold text-foreground">{fr ? 'Délai d\'accès' : 'Access time'}:</span> <span className="text-muted-foreground">{training.rncp.delaiAcces}</span></div>
                <div><span className="font-semibold text-foreground">{fr ? 'Taux de réussite' : 'Success rate'}:</span> <span className="text-muted-foreground">{training.rncp.tauxReussite}</span></div>
                <div><span className="font-semibold text-foreground">{fr ? 'Taux d\'insertion' : 'Employment rate'}:</span> <span className="text-muted-foreground">{training.rncp.tauxInsertion}</span></div>
                <div><span className="font-semibold text-foreground">Contact:</span> <span className="text-muted-foreground">{training.rncp.contact}</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">{fr ? 'Questions fréquentes' : 'Frequently asked questions'}</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {training.faq.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border border-border px-6">
                <AccordionTrigger className="text-left text-base font-medium text-foreground hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(var(--primary))]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-primary-foreground mb-4">{fr ? 'Prêt à vous former ?' : 'Ready to start?'}</h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            {fr ? 'Éligible OPCO, FIF-PL et CPF. Attestation de formation officielle incluse.' : 'OPCO, FIF-PL and CPF eligible. Official training certificate included.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleEnroll} size="lg" variant="secondary">{fr ? "S'inscrire" : 'Enroll'}</Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <a href="#contact-formation">{fr ? 'Nous contacter' : 'Contact us'}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <TrainingContactForm training={training} fr={fr} />

      {/* Coming Soon Dialog */}
      <AlertDialog open={showComingSoon} onOpenChange={setShowComingSoon}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {fr ? '🚀 Bientôt disponible !' : '🚀 Coming soon!'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {fr
                ? 'Les inscriptions en ligne pour les formations éligibles au financement (OPCO, FIF-PL, CPF) seront bientôt ouvertes. En attendant, contactez-nous pour réserver votre place ou obtenir un devis personnalisé.'
                : 'Online enrollment for funded training programs (OPCO, FIF-PL, CPF) will be available soon. In the meantime, contact us to reserve your spot or get a custom quote.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button asChild variant="default">
              <Link to="/contact">{fr ? 'Nous contacter' : 'Contact us'}</Link>
            </Button>
            <AlertDialogAction>{fr ? 'Compris' : 'Got it'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
